import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { FormattedMessage } from 'react-intl';
import { ArrowForward } from '@material-ui/icons';
import SponsorsTable from 'components/Dashboard/WorldCup/Sponsors/SponsorsTable';
import Loading from 'components/Basic/Loading';
import SponsorsFormModal from 'components/Dashboard/WorldCup/Sponsors/SponsorsFormModal';
import SponsorsFilter from 'components/Dashboard/WorldCup/Sponsors/SponsorsFilter';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import { useLazyGetSponsorsListQuery } from 'core/worldCup/sponsors/SponsorsService';
import { setSponsorsData } from 'core/worldCup/sponsors/SponsorsSlice';

const Sponsors: React.FC = () => {
  const [state, setState] = useState({
    isLoading: false,
    isShowDetailModal: false,
    searchText: '',
    sortStatus: '',
    filterStatus: '',
    sortName: '',
    sortId: 'desc',
  });
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const dispatch = useAppDispatch();
  const [getSponsorsList] = useLazyGetSponsorsListQuery();

  const { list } = useAppSelector(state => state.sponsorsReducer);

  useEffect(() => {
    getInitialData();
    setState({ ...state, searchText: '' });
  }, []);

  useEffect(() => {
    setCurrent(1);
  }, [pageSize]);

  const getInitialData = async () => {
    setState({ ...state, isLoading: true });

    const queryString = buildQueryString({
      search: '',
      page: 1,
      size: pageSize,
      count: true,
      sort_by_id: state.sortId,
      filter: state.filterStatus,
    });

    try {
      const res = await getSponsorsList({ queryString }).unwrap();
      dispatch(setSponsorsData({ list: res }));
    } catch (error) {
      console.error('Error fetching sponsors list:', error);
    } finally {
      setState({ ...state, isLoading: false });
    }
  };

  const handleChangePage = async (page: number, size: number, search = '') => {
    setState(prev => ({ ...prev, isLoading: true }));
    setPageSize(size);

    const queryString = buildQueryString({
      search: state.searchText.trim(),
      page: page,
      size: size,
      count: true,
      sort_by_status: state.sortStatus,
      sort_by_id: state.sortId,
      sort_by_name: state.sortName,
      filter: state.filterStatus,
    });

    try {
      const res = await getSponsorsList({ queryString }).unwrap();
      dispatch(setSponsorsData({ list: res }));
      setState(prev => ({ ...prev, isLoading: false }));
    } catch (e) {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleSearch = async (e: any) => {
    const searchTextValue = e.target.value.trimStart();
    setState({ ...state, searchText: searchTextValue });
    if (e.keyCode === 13) {
      setCurrent(1);

      const queryString = buildQueryString({
        search: e.target.value.trim(),
        page: 1,
        size: pageSize,
        count: true,
        sort_by_status: state.sortStatus,
        sort_by_id: state.sortId,
        sort_by_name: state.sortName,
        filter: state.filterStatus,
      });

      try {
        const res = await getSponsorsList({ queryString }).unwrap();
        dispatch(setSponsorsData({ list: res }));
      } catch (e) {
        console.log(e);
      }
    }
  };

  const handleAvailableFilter = (value: string) => {
    setState({ ...state, filterStatus: value });
  };

  const handleSort = (value: string) => {
    let newState = { ...state };
    if (value[0] === 'I') {
      newState = {
        ...newState,
        sortId: value.substring(1),
        sortName: '',
        sortStatus: '',
      };
    } else if (value[0] === 'N') {
      newState = {
        ...newState,
        sortName: value.substring(1),
        sortId: '',
        sortStatus: '',
      };
    } else if (value[0] === 'S') {
      newState = {
        ...newState,
        sortStatus: value.substring(1),
        sortId: '',
        sortName: '',
      };
    }
    setState(newState);
  };

  const onApplyFilter = async () => {
    setState({ ...state, isLoading: true });
    setCurrent(1);

    const queryString = buildQueryString({
      search: state.searchText.trim(),
      page: 1,
      size: pageSize,
      count: true,
      sort_by_status: state.sortStatus,
      sort_by_id: state.sortId,
      sort_by_name: state.sortName,
      filter: state.filterStatus,
    });

    try {
      const res = await getSponsorsList({ queryString }).unwrap();
      dispatch(setSponsorsData({ list: res }));
    } catch (e) {
      console.log(e);
    } finally {
      setState({ ...state, isLoading: false });
    }
  };

  return (
    <div className="sponsors_wrapper" id="sponsors_wrapper" style={{ position: 'relative' }}>
      <Loading visible={state.isLoading} />
      <div className="btn_wrapper" id="new_sponsor_btn_wrapper">
        <Button
          className="add_btn"
          onClick={() => setState({ ...state, isShowDetailModal: true })}
          id="new_sponsor_btn"
        >
          <FormattedMessage id="Add new sponsor" />
          <ArrowForward />
        </Button>
      </div>
      <SponsorsFilter
        searchText={state.searchText}
        handleSearch={handleSearch}
        onAvailableFilter={handleAvailableFilter}
        onSort={handleSort}
        onApplyFilter={onApplyFilter}
      />
      <SponsorsTable
        sponsorsData={list.data || null}
        onChangePage={(current, size) => handleChangePage(current, size)}
        total={list.meta?.totalCount || 0}
        setCurrent={setCurrent}
        current={current}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
      <SponsorsFormModal
        mode="modal"
        isOpenModal={state.isShowDetailModal}
        onCloseModal={() => setState({ ...state, isShowDetailModal: false })}
        onSaveSuccess={() => handleChangePage(current, pageSize)}
        sponsor={null}
      />
    </div>
  );
};

Sponsors.defaultProps = {};

export default Sponsors;

const buildQueryString = ({
  search = '',
  page = 1,
  size = 10,
  count = true,
  sort_by_id = 'desc',
  sort_by_status = '',
  sort_by_name = '',
  filter = '',
}) => {
  const params = [`search=${encodeURIComponent(search)}`, `page=${page}`, `limit=${size}`];

  if (sort_by_status) {
    params.push(`sort=isActive`);
    params.push(`sortDirection=${sort_by_status}`);
  }

  if (sort_by_id) {
    params.push(`sort=id`);
    params.push(`sortDirection=${sort_by_id}`);
  }

  if (sort_by_name) {
    params.push(`sort=name`);
    params.push(`sortDirection=${sort_by_name}`);
  }

  if (filter) {
    params.push(`isActive=${filter}`);
  }

  return params.join('&');
};
